// src/components/PlayersPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  getMyPlayers, createPlayer, updatePlayer, deletePlayer,
  searchPlayers, linkPlayerToUser, unlinkPlayerFromUser, updateUserPlayer,
  findSimilarPlayers, updatePlayerRanking
} from '../services/playersService';

import { refs } from '../data/refs';
import DuplicatePlayersModal from './DuplicatePlayersModal';
import PlayersMergePanel from './PlayersMergePanel';
import PlayerRankingModal from './PlayerRankingModal';
import { useSubscription } from '../hooks/useSubscription';

const PAYS_OPTIONS = [
  { code: 'FRA', label: 'France' }, { code: 'ESP', label: 'Espagne' },
  { code: 'USA', label: 'États-Unis' }, { code: 'GBR', label: 'Grande-Bretagne' },
  { code: 'GER', label: 'Allemagne' }, { code: 'ITA', label: 'Italie' },
  { code: 'SUI', label: 'Suisse' }, { code: 'BEL', label: 'Belgique' },
  { code: 'ARG', label: 'Argentine' }, { code: 'AUS', label: 'Australie' },
];

const EMPTY_FORM = {
  nom: '', prenom: '', dateNaissance: '', sexe: '',
  paysCode: '', mainDominante: '', dernierClassementCode: '',
  dernierClub: '', isMyProfile: false, commentaire: '',
};

export default function PlayersPage({ currentUser, onBack, isAdmin: isAdminProp }) {
  const { isAdmin: isAdminProfile } = useSubscription();
  const isAdmin = !!(isAdminProp || isAdminProfile);
  const [myPlayers, setMyPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [dupDialog, setDupDialog] = useState(null);
  const [rankingPlayer, setRankingPlayer] = useState(null);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('mes-joueurs'); // 'mes-joueurs' | 'recherche'

  const loadMyPlayers = useCallback(async () => {
    setLoading(true);
    const result = await getMyPlayers();
    if (result.success) setMyPlayers(result.players);
    setLoading(false);
  }, []);

  useEffect(() => { loadMyPlayers(); }, [loadMyPlayers]);

  // Recherche globale avec debounce
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      const result = await searchPlayers(searchQuery);
      if (result.success) setSearchResults(result.players);
      setSearching(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleOpenCreate = () => {
    setEditingPlayer(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowForm(true);
  };

  const handleOpenEdit = (player) => {
    setEditingPlayer(player);
    setForm({
      nom: player.nom || '',
      prenom: player.prenom || '',
      dateNaissance: player.dateNaissance || '',
      sexe: player.sexe?.trim() || '',
      paysCode: player.paysCode || '',
      mainDominante: player.mainDominante || '',
      dernierClassementCode: player.dernierClassementCode || '',
      dernierClub: player.dernierClub || '',
      isMyProfile: player.isMyProfile || false,
      commentaire: player.commentaire || '',
    });
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nom.trim()) { setError('Le nom est obligatoire'); return; }

    // Vérifier unicité du profil joueur
    if (form.isMyProfile) {
      const existingProfile = myPlayers.find(p =>
        p.isMyProfile && p.id !== editingPlayer?.id
      );
      if (existingProfile) {
        setError(`Vous avez déjà un profil joueur : ${[existingProfile.prenom, existingProfile.nom].filter(Boolean).join(' ')}. Décochez "C'est mon profil joueur" ou modifiez l'existant.`);
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      if (editingPlayer) {
        // Mise à jour joueur
        const r = await updatePlayer(editingPlayer.id, form);
        if (!r.success) { setError(r.error); setSaving(false); return; }
        // Classement modifié : on l'enregistre aussi dans l'historique
        if (form.dernierClassementCode && form.dernierClassementCode !== String(editingPlayer.dernierClassementCode || '').trim()) {
          await updatePlayerRanking(editingPlayer.id, {
            classementCode: form.dernierClassementCode,
            club: form.dernierClub || null,
            source: 'manual',
          });
        }
        // Mise à jour liaison (commentaire + isMyProfile)
        if (editingPlayer.userPlayerId) {
          await updateUserPlayer(editingPlayer.userPlayerId, {
            isMyProfile: form.isMyProfile,
            commentaire: form.commentaire,
          });
        }
      } else {
        // Détection de doublons avant création
        const sim = await findSimilarPlayers(form.nom, form.prenom);
        if (sim.success && sim.players.length > 0) {
          const chosen = await new Promise((resolve) => {
            setDupDialog({
              candidates: sim.players,
              searched: { nom: form.nom, prenom: form.prenom },
              onUse: (player) => { setDupDialog(null); resolve(player); },
              onCreateNew: () => { setDupDialog(null); resolve(null); },
              onCancel: () => { setDupDialog(null); resolve('cancel'); },
            });
          });
          if (chosen === 'cancel') { setSaving(false); return; } // on reste sur le formulaire
          if (chosen) {
            const l = await linkPlayerToUser(chosen.id, { isMyProfile: form.isMyProfile, commentaire: form.commentaire });
            if (!l.success) { setError(l.error); setSaving(false); return; }
            await loadMyPlayers();
            setShowForm(false);
            setSaving(false);
            return;
          }
        }
        const r = await createPlayer(form);
        if (!r.success) { setError(r.error); setSaving(false); return; }
      }
      await loadMyPlayers();
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleLinkPlayer = async (player) => {
    const alreadyLinked = myPlayers.some(p => p.id === player.id);
    if (alreadyLinked) { alert('Ce joueur est déjà dans votre liste.'); return; }
    const r = await linkPlayerToUser(player.id);
    if (r.success) {
      await loadMyPlayers();
      setActiveTab('mes-joueurs');
      setSearchQuery('');
      setSearchResults([]);
    } else {
      alert('Erreur : ' + r.error);
    }
  };

  const handleUnlink = async (player) => {
    if (!player.userPlayerId) return;
    const r = await unlinkPlayerFromUser(player.userPlayerId);
    if (r.success) await loadMyPlayers();
    else alert('Erreur : ' + r.error);
    setDeleteConfirm(null);
  };

  const handleDeleteGlobal = async (player) => {
    const r = await deletePlayer(player.id);
    if (r.success) await loadMyPlayers();
    else alert('Erreur : ' + r.error);
    setDeleteConfirm(null);
  };

  const canEdit = (player) => {
    return isAdmin || player.createdBy === currentUser?.id;
  };

  const formatName = (p) => [p.prenom, p.nom].filter(Boolean).join(' ') || '(Sans nom)';

  // Convertit le code de classement stocké en base (ex: "32") en label (ex: "30/1")
  // Si le code est introuvable (ancien joueur enregistré avec un label), on affiche la valeur brute
  const getClassementLabel = (code) => {
    if (!code) return '';
    const found = refs.refClassement.find(c => c.code === String(code).trim());
    return found ? found.labelFr : code;
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #ddd', fontSize: '14px',
    boxSizing: 'border-box', backgroundColor: 'var(--bg-card)',
    color: 'var(--text-primary)',
  };

  const labelStyle = { fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' };

  return (
    <div style={{ padding: '16px', fontFamily: 'Arial, sans-serif', maxWidth: '600px', margin: '0 auto' }}>
      {dupDialog && <DuplicatePlayersModal {...dupDialog} />}
      {rankingPlayer && (
        <PlayerRankingModal
          player={rankingPlayer}
          onClose={() => setRankingPlayer(null)}
          onUpdated={loadMyPlayers}
        />
      )}
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: '14px', color: '#1976d2', cursor: 'pointer' }}>
          ← Retour
        </button>
        <h2 style={{ margin: 0, fontSize: '18px' }}>👤 Mes joueurs</h2>
        <button
          onClick={handleOpenCreate}
          style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', backgroundColor: '#1976d2', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
        >
          + Nouveau
        </button>
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '2px solid #e0e0e0' }}>
        {['mes-joueurs', 'recherche', ...(isAdmin ? ['fusion'] : [])].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 16px', border: 'none', background: 'none',
              borderBottom: activeTab === tab ? '3px solid #1976d2' : '3px solid transparent',
              color: activeTab === tab ? '#1976d2' : '#666',
              fontWeight: activeTab === tab ? '600' : '400',
              fontSize: '14px', cursor: 'pointer',
            }}
          >
            {tab === 'mes-joueurs' ? `Ma liste (${myPlayers.length})` : tab === 'recherche' ? '🔍 Rechercher' : '🔀 Fusion'}
          </button>
        ))}
      </div>

      {/* Onglet Fusion (admin) */}
      {activeTab === 'fusion' && isAdmin && <PlayersMergePanel />}

      {/* Onglet Ma liste */}
      {activeTab === 'mes-joueurs' && (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '40px' }}>Chargement...</div>
          ) : myPlayers.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>👤</div>
              <div>Aucun joueur dans votre liste.</div>
              <div style={{ fontSize: '12px', marginTop: '8px' }}>Créez un joueur ou recherchez-en un existant.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {myPlayers.map(player => (
                <div
                  key={player.id}
                  style={{
                    backgroundColor: 'var(--bg-card)', borderRadius: '10px',
                    padding: '12px 14px', border: '1px solid #e0e0e0',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: '600', fontSize: '15px' }}>{formatName(player)}</span>
                      {player.isMyProfile && <span style={{ fontSize: '11px', backgroundColor: '#e3f2fd', color: '#1976d2', padding: '2px 6px', borderRadius: '4px' }}>Mon profil</span>}
                      {player.isVerified && <span style={{ fontSize: '11px', backgroundColor: '#e8f5e9', color: '#388e3c', padding: '2px 6px', borderRadius: '4px' }}>✓ Vérifié</span>}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {player.dernierClassementCode && <span>🏆 {getClassementLabel(player.dernierClassementCode)}</span>}
                      {player.mainDominante && <span>🖐 {player.mainDominante}</span>}
                      {player.dernierClub && <span>🎾 {player.dernierClub}</span>}
                    </div>
                    {player.commentaire && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
                        💬 {player.commentaire}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginLeft: '8px' }}>
                    <button
                      onClick={() => setRankingPlayer(player)}
                      style={{ width: '32px', height: '32px', borderRadius: '6px', border: 'none', backgroundColor: '#7e57c2', color: 'white', cursor: 'pointer', fontSize: '14px' }}
                      title="Classement et historique"
                    >📈</button>
                    {canEdit(player) && (
                      <button
                        onClick={() => handleOpenEdit(player)}
                        style={{ width: '32px', height: '32px', borderRadius: '6px', border: 'none', backgroundColor: '#2196f3', color: 'white', cursor: 'pointer', fontSize: '14px' }}
                        title="Modifier"
                      >✏️</button>
                    )}
                    <button
                      onClick={() => setDeleteConfirm({ player, type: 'unlink' })}
                      style={{ width: '32px', height: '32px', borderRadius: '6px', border: 'none', backgroundColor: '#ff9800', color: 'white', cursor: 'pointer', fontSize: '14px' }}
                      title="Retirer de ma liste"
                    >−</button>
                    {isAdmin && (
                      <button
                        onClick={() => setDeleteConfirm({ player, type: 'delete' })}
                        style={{ width: '32px', height: '32px', borderRadius: '6px', border: 'none', backgroundColor: '#f44336', color: 'white', cursor: 'pointer', fontSize: '14px' }}
                        title="Supprimer définitivement"
                      >🗑️</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Onglet Recherche */}
      {activeTab === 'recherche' && (
        <div>
          <input
            type="text"
            placeholder="Rechercher par nom ou prénom..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ ...inputStyle, marginBottom: '12px' }}
            autoFocus
          />
          {searching && <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>Recherche...</div>}
          {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
              Aucun résultat pour "{searchQuery}"
              <div style={{ marginTop: '8px' }}>
                <button onClick={handleOpenCreate} style={{ color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '14px' }}>
                  Créer ce joueur
                </button>
              </div>
            </div>
          )}
          {searchResults.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {searchResults.map(player => {
                const alreadyLinked = myPlayers.some(p => p.id === player.id);
                return (
                  <div
                    key={player.id}
                    style={{
                      backgroundColor: 'var(--bg-card)', borderRadius: '10px',
                      padding: '12px 14px', border: '1px solid #e0e0e0',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      opacity: alreadyLinked ? 0.6 : 1,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '15px' }}>{formatName(player)}</div>
                      <div style={{ fontSize: '12px', color: '#999', marginTop: '2px', display: 'flex', gap: '10px' }}>
                        {player.dernierClassementCode && <span>🏆 {getClassementLabel(player.dernierClassementCode)}</span>}
                        {player.dernierClub && <span>🎾 {player.dernierClub}</span>}
                        {player.isVerified && <span style={{ color: '#388e3c' }}>✓ Vérifié</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleLinkPlayer(player)}
                      disabled={alreadyLinked}
                      style={{
                        padding: '6px 12px', borderRadius: '6px', border: 'none',
                        backgroundColor: alreadyLinked ? '#e0e0e0' : '#4caf50',
                        color: alreadyLinked ? '#999' : 'white',
                        fontSize: '13px', cursor: alreadyLinked ? 'default' : 'pointer', fontWeight: '500',
                      }}
                    >
                      {alreadyLinked ? 'Déjà ajouté' : '+ Ajouter'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal formulaire création/édition */}
      {showForm && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 2000 }}
          onClick={() => setShowForm(false)}
        >
          <div
            style={{ backgroundColor: 'var(--bg-card)', borderRadius: '16px 16px 0 0', padding: '24px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px', fontSize: '17px' }}>
              {editingPlayer ? '✏️ Modifier le joueur' : '➕ Nouveau joueur'}
            </h3>

            {error && (
              <div style={{ backgroundColor: '#ffebee', color: '#c62828', padding: '10px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Nom *</label>
                <input style={inputStyle} value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="Nom de famille" />
              </div>
              <div>
                <label style={labelStyle}>Prénom</label>
                <input style={inputStyle} value={form.prenom} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))} placeholder="Prénom" />
              </div>
              <div>
                <label style={labelStyle}>Date de naissance</label>
                <input type="date" style={inputStyle} value={form.dateNaissance} onChange={e => setForm(f => ({ ...f, dateNaissance: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>Sexe</label>
                <select style={inputStyle} value={form.sexe} onChange={e => setForm(f => ({ ...f, sexe: e.target.value }))}>
                  <option value="">--</option>
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Main dominante</label>
                <select style={inputStyle} value={form.mainDominante} onChange={e => setForm(f => ({ ...f, mainDominante: e.target.value }))}>
                  <option value="">--</option>
                  <option value="Droitier">Droitier</option>
                  <option value="Gaucher">Gaucher</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Classement</label>
                <select style={inputStyle} value={form.dernierClassementCode} onChange={e => setForm(f => ({ ...f, dernierClassementCode: e.target.value }))}>
                  <option value="">--</option>
                  {refs.refClassement.map(c => <option key={c.code} value={c.code}>{c.labelFr}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Pays</label>
                <select style={inputStyle} value={form.paysCode} onChange={e => setForm(f => ({ ...f, paysCode: e.target.value }))}>
                  <option value="">--</option>
                  {PAYS_OPTIONS.map(p => <option key={p.code} value={p.code}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Club</label>
                <input style={inputStyle} value={form.dernierClub} onChange={e => setForm(f => ({ ...f, dernierClub: e.target.value }))} placeholder="Nom du club" />
              </div>
            </div>

            {/* Commentaire */}
            <div style={{ marginTop: '12px' }}>
              <label style={labelStyle}>Commentaire</label>
              <textarea
                style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
                value={form.commentaire}
                onChange={e => setForm(f => ({ ...f, commentaire: e.target.value }))}
                placeholder="Notes personnelles sur ce joueur..."
              />
            </div>

            {/* Mon profil */}
            <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                id="isMyProfile"
                checked={form.isMyProfile}
                onChange={e => setForm(f => ({ ...f, isMyProfile: e.target.checked }))}
              />
              <label htmlFor="isMyProfile" style={{ fontSize: '14px', cursor: 'pointer' }}>
                C'est mon profil joueur
              </label>
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button
                onClick={() => setShowForm(false)}
                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #ddd', backgroundColor: 'white', color: '#333', fontSize: '14px', cursor: 'pointer' }}
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ flex: 2, padding: '12px', borderRadius: '8px', border: 'none', backgroundColor: '#1976d2', color: 'white', fontSize: '14px', fontWeight: '600', cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}
              >
                {saving ? 'Enregistrement...' : (editingPlayer ? 'Enregistrer' : 'Créer le joueur')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmation suppression */}
      {deleteConfirm && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', maxWidth: '360px', width: '90%' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 12px', fontSize: '17px' }}>
              {deleteConfirm.type === 'delete' ? '🗑️ Supprimer définitivement ?' : '− Retirer de ma liste ?'}
            </h3>
            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 20px' }}>
              <strong>{formatName(deleteConfirm.player)}</strong>
              {deleteConfirm.type === 'delete'
                ? ' sera supprimé définitivement de la base. Cette action est irréversible.'
                : ' sera retiré de votre liste mais restera dans la base globale.'}
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd', backgroundColor: 'white', cursor: 'pointer' }}>
                Annuler
              </button>
              <button
                onClick={() => deleteConfirm.type === 'delete' ? handleDeleteGlobal(deleteConfirm.player) : handleUnlink(deleteConfirm.player)}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', backgroundColor: deleteConfirm.type === 'delete' ? '#f44336' : '#ff9800', color: 'white', cursor: 'pointer', fontWeight: '600' }}
              >
                {deleteConfirm.type === 'delete' ? 'Supprimer' : 'Retirer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}